import { Choice, InlineError, TextField } from "@cmsgov/ds-medicare-gov";
import classNames from "classnames";
import React, { ChangeEvent } from "react";
import { County } from "../../../@types";
import { useTranslate } from "../../../helpers";
import { ErrorState } from "../CoverageSelector.v2";

interface ZipcodeAndCoverageYearStepProps {
  countiesErrorState: ErrorState;
  countyResults: County[];
  currentFips: string | undefined;
  fetching: boolean;
  initialFips: string | undefined;
  isOpenEnrollmentNextYearOnly?: boolean;
  mctCurrentPlanYear: number;
  onCountyChange: (county: string) => void;
  onCoverageYearChange: (selectedYear: string) => void;
  onZipcodeChange: (zipcodeInput: string) => void;
  selectedCoverageYear?: number;
  selectedZipcode?: string;
  showCoverageYearSelector?: boolean;
  zipcode?: string;
  zipcodeErrorState: ErrorState;
}

export const ZipcodeAndCoverageYearStep: React.FunctionComponent<ZipcodeAndCoverageYearStepProps> = ({
  countiesErrorState,
  countyResults,
  currentFips,
  fetching,
  initialFips,
  isOpenEnrollmentNextYearOnly,
  mctCurrentPlanYear,
  onCountyChange,
  onCoverageYearChange,
  onZipcodeChange,
  selectedCoverageYear,
  showCoverageYearSelector,
  zipcode,
  zipcodeErrorState,
}) => {
  const t = useTranslate();
  const nextPlanYear = mctCurrentPlanYear + 1;
  return (
    <div>
      {showCoverageYearSelector && (
        <div
          data-testid="coverage-year-section"
          className="ds-u-margin-bottom--2"
        >
          <h2 className="ds-text-heading--md ds-u-measure--narrow ds-u-leading--lead">
            {t("coverage_selector.oe.zipcode.heading")}
          </h2>
          <fieldset className="ds-c-fieldset ds-u-margin-top--0">
            <legend className="ds-c-label">
              <span className="ds-u-text-transform--uppercase">
                {t("coverage_selector.coverage_for")}
              </span>
            </legend>
            <div className="mct-c-coverage-selector-v2__coverage-years">
              <Choice
                type="radio"
                name="coverage-year"
                className="mct-c-coverage-selector-v2__coverage-year-choice--next-year"
                id={"coverage-year-next-year"}
                value={nextPlanYear}
                data-testid={`coverage-selector-coverage-year-${nextPlanYear}`}
                data-cy={"coverage-year-next-year"}
                checked={selectedCoverageYear === nextPlanYear}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  onCoverageYearChange(e.target.value)
                }
              >
                {nextPlanYear}
              </Choice>
              {!isOpenEnrollmentNextYearOnly && (
                <Choice
                  type="radio"
                  className="mct-c-coverage-selector-v2__coverage-year-choice--current-year"
                  name="coverage-year"
                  id={"coverage-year-current-year"}
                  value={mctCurrentPlanYear}
                  data-testid={`coverage-selector-coverage-year-${mctCurrentPlanYear}`}
                  data-cy={"coverage-year-current-year"}
                  checked={selectedCoverageYear === mctCurrentPlanYear}
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    onCoverageYearChange(e.target.value)
                  }
                >
                  {mctCurrentPlanYear}
                </Choice>
              )}
            </div>
          </fieldset>
        </div>
      )}
      {!showCoverageYearSelector && (
        <h2 className="ds-text-heading--md mct-c-coverage-selector-v2__step-heading">
          {t("coverage_selector.ooe.zipcode.heading")}
        </h2>
      )}
      <div className="mct-c-coverage-selector-v2__zipcode-container">
        <TextField
          label={t("coverage_selector.zip_code")}
          labelClassName="ds-h6"
          name="zipcode"
          data-testid="coverage-selector-zipcode"
          className={classNames("mct-c-coverage-selector-v2__zipcode", {
            "mct-c-coverage-selector-v2__text-field--error":
              zipcodeErrorState.error,
          })}
          onChange={e => onZipcodeChange(e.target.value)}
          defaultValue={zipcode}
          aria-required="true"
          aria-invalid={!!zipcodeErrorState.error}
          {...(zipcodeErrorState.error
            ? { "aria-describedby": "zipcode-container-error" }
            : {})}
        />
      </div>
      {zipcodeErrorState.message && (
        <div
          className=" ds-u-padding-top--1"
          data-testid="zipcodePlanTypeError"
        >
          <InlineError id="zipcode-container-error">
            {t(zipcodeErrorState.message || "")}
          </InlineError>
        </div>
      )}
      {fetching && <p>{t("app.loading")}...</p>}
      <>
        {countyResults.length > 1 && (
          <fieldset className="ds-c-fieldset ds-u-margin-top--2">
            <legend className="ds-c-label ds-u-margin-bottom--1">
              <span data-testid="select-your-county">
                {t("coverage_selector.select_your_county")}
              </span>
            </legend>
            <div className="mct-c-coverage-selector-v2__zip-counties">
              {countyResults.map((county, i) => (
                <Choice
                  key={i}
                  className="mct-c-coverage-selector-v2__county-choice"
                  type="radio"
                  name="county"
                  data-testid={`coverage-selector-fips-${county.fips}`}
                  id={county.fips}
                  defaultChecked={
                    county.fips === initialFips || county.fips === currentFips
                  }
                  value={JSON.stringify(county)}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => {
                    onCountyChange(e.target.value);
                  }}
                >
                  {county.name}, {county.state}
                </Choice>
              ))}
            </div>
          </fieldset>
        )}
        {countiesErrorState.error && (
          <div className="ds-u-margin-y--1">
            <InlineError>{t(countiesErrorState.message || "")}</InlineError>
          </div>
        )}
      </>
    </div>
  );
};
